'use es6';

import styled from 'foundations-theming/utils';
import { useContext } from 'react';
import PropTypes from 'prop-types';
import ErrorBoundary from 'customer-data-objects-ui-components/ErrorBoundary';
import SidebarError from 'customer-data-sidebar/SidebarError';
import { SUBSCRIPTION_TYPE_ID, COMMERCE_PAYMENT_TYPE_ID, INVOICE_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { CART, QUOTE, ORDER, PRODUCT } from 'customer-data-objects/constants/ObjectTypes';
import PaymentHighlight from 'card-payment-highlight-lib';
import InvoiceHighlight from 'invoices-highlight-card-lib';
import QuoteHighlight from 'quotes-highlight-card-lib';
import UniversalSidebarHighlight from './highlight/UniversalSidebarHighlight';
import SubscriptionHighlight from 'card-subscription-highlight-lib';
import { useHostAppCallbacksContext } from './context/HostAppCallbacksContext';
import { CardLocationContext } from './context/CardLocationContext';
import { usePermissionsContext } from './context/PermissionsContext';
import { PreviewConfigContext, PreviewConfigRecord } from './context/PreviewConfigContext';
import { CartHighlight, OrderHighlight } from 'orders-highlight-card-lib';
import ProductHighlight from 'products-highlight-card-lib';
import { jsx as _jsx } from "react/jsx-runtime";
const LegacyStyledHighlight = styled.div.withConfig({
  displayName: "UniversalSidebarHighlightWrapper__LegacyStyledHighlight",
  componentId: "sc-12jkzml-0"
})(["border-bottom:1px solid ", ";"], ({
  theme
}) => theme.color['do-not-use-battleship']);
const PreviewStyledHighlight = styled.div.withConfig({
  displayName: "UniversalSidebarHighlightWrapper__PreviewStyledHighlight",
  componentId: "sc-12jkzml-1"
})(["border-bottom:1px solid ", ";padding-bottom:24px;"], ({
  theme
}) => theme.color['do-not-use-battleship']);
const UniversalSidebarHighlightWrapper = ({
  auth,
  HighlightComponent,
  objectId,
  objectType
}) => {
  const {
    onObjectUpdate,
    onObjectDelete,
    onObjectCreate
  } = useHostAppCallbacksContext();
  const {
    cardLocation
  } = useContext(CardLocationContext);
  const {
    closeSidebar,
    setPreviewConfig
  } = useContext(PreviewConfigContext);
  const permissions = usePermissionsContext();

  // consumers pass in null when they are using their own header component
  if (HighlightComponent === null) {
    return null;
  }

  // New object type documentation: https://paper.dropbox.com/doc/New-Object-Type-Support-in-Sidebar--BE6TIaoe~oiPO12UZPn9dM06Ag-o1pzC9glwkw6vwaxzAnq9
  if (objectType === SUBSCRIPTION_TYPE_ID) {
    return /*#__PURE__*/_jsx(SubscriptionHighlight, {
      objectId: objectId,
      auth: auth,
      permissions: permissions,
      onObjectUpdate: onObjectUpdate,
      closeSidebar: closeSidebar
    });
  }

  //   New object type documentation: https://paper.dropbox.com/doc/New-Object-Type-Support-in-Sidebar--BE6TIaoe~oiPO12UZPn9dM06Ag-o1pzC9glwkw6vwaxzAnq9
  if (objectType === CART) {
    return /*#__PURE__*/_jsx(CartHighlight, {
      objectId: objectId,
      closeSidebar: closeSidebar
    });
  }

  //   New object type documentation: https://paper.dropbox.com/doc/New-Object-Type-Support-in-Sidebar--BE6TIaoe~oiPO12UZPn9dM06Ag-o1pzC9glwkw6vwaxzAnq9
  if (objectType === ORDER) {
    return /*#__PURE__*/_jsx(OrderHighlight, {
      objectId: objectId,
      closeSidebar: closeSidebar
    });
  }

  // New object type documentation: https://paper.dropbox.com/doc/New-Object-Type-Support-in-Sidebar--BE6TIaoe~oiPO12UZPn9dM06Ag-o1pzC9glwkw6vwaxzAnq9
  if (objectType === COMMERCE_PAYMENT_TYPE_ID) {
    return /*#__PURE__*/_jsx(PaymentHighlight, {
      objectId: objectId,
      permissions: permissions,
      onObjectUpdate: onObjectUpdate,
      onObjectDelete: onObjectDelete,
      closeSidebar: closeSidebar,
      auth: auth
    });
  }

  // New object type documentation: https://paper.dropbox.com/doc/New-Object-Type-Support-in-Sidebar--BE6TIaoe~oiPO12UZPn9dM06Ag-o1pzC9glwkw6vwaxzAnq9
  if (objectType === INVOICE_TYPE_ID) {
    return /*#__PURE__*/_jsx(InvoiceHighlight, {
      objectId: objectId,
      permissions: permissions,
      closeSidebar: closeSidebar,
      onObjectUpdate: onObjectUpdate,
      onObjectCreate: onObjectCreate,
      updatePreview: (previewObjectTypeId, previewObjectId) => setPreviewConfig(PreviewConfigRecord({
        objectTypeId: previewObjectTypeId,
        objectId: previewObjectId
      }))
    });
  }

  // New object type documentation: https://paper.dropbox.com/doc/New-Object-Type-Support-in-Sidebar--BE6TIaoe~oiPO12UZPn9dM06Ag-o1pzC9glwkw6vwaxzAnq9
  if (objectType === QUOTE) {
    return /*#__PURE__*/_jsx(QuoteHighlight, {
      objectId: objectId,
      permissions: permissions,
      closeSidebar: closeSidebar
    });
  }
  if (objectType === PRODUCT) {
    return /*#__PURE__*/_jsx(ProductHighlight, {
      gates: auth.gates,
      objectId: objectId,
      onObjectCreate: onObjectCreate
    });
  }
  const StyledHighlight = cardLocation === 'CRM_PREVIEW' ? LegacyStyledHighlight : PreviewStyledHighlight;
  if (HighlightComponent) {
    return /*#__PURE__*/_jsx(StyledHighlight, {
      "data-selenium-test": "universal-sidebar-highlight",
      "data-test-id": "universal-sidebar-highlight",
      children: /*#__PURE__*/_jsx(ErrorBoundary, {
        ErrorComponent: SidebarError,
        boundaryName: "UniversalSidebarHighlightWrapper_SidebarError",
        showRefreshAlert: false,
        children: /*#__PURE__*/_jsx(HighlightComponent, {
          auth: auth,
          objectType: objectType,
          objectId: objectId,
          subjectId: objectId,
          onObjectUpdate: onObjectUpdate
        })
      })
    });
  }

  // Fallback highlight for when no HighlightComponent (undefined) is provided
  return /*#__PURE__*/_jsx(UniversalSidebarHighlight, {
    objectId: objectId,
    objectType: objectType
  });
};
UniversalSidebarHighlightWrapper.propTypes = {
  auth: PropTypes.shape({
    user: PropTypes.shape({
      email: PropTypes.string
    }),
    gates: PropTypes.array
  }),
  HighlightComponent: PropTypes.elementType,
  objectId: PropTypes.string.isRequired,
  objectType: PropTypes.string.isRequired
};
export default UniversalSidebarHighlightWrapper;